import { useCallback, useEffect, useRef } from "react";
import {
  holdResumePlayback,
  releaseResumePlayback,
} from "@/features/tutor/vo-demo/runtime/resumePlaybackHold";
import { addResumeDebugEvent } from "@/store/useResumeDebugStore";

// A backstop, and deliberately not a budget: the restore releases this itself
// on both of its own endings, applying the payload and running out of retries.
// Reaching this timer means neither happened, so there is no duration to
// derive -- only an upper bound on how long a path nobody has thought of may
// keep the tutor silent.
const RESUME_PLAYBACK_HOLD_TIMEOUT_MS = 8000;

/**
 * Hold the tutor's resume turn until the restored bite is on screen.
 *
 * Armed before the boot asks for the turn, released once the rehydrate payload
 * has been applied. Every other release is a safety net: a payload that never
 * applies must end as a late greeting, never as silence.
 */
export function useResumePlaybackHold({
  pauseTutorCaptions,
  resumeTutorCaptions,
}) {
  const isHeldRef = useRef(false);
  const timeoutRef = useRef(null);

  const releaseResumePlaybackHold = useCallback(
    (reason = "unknown") => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (!isHeldRef.current) return;
      isHeldRef.current = false;
      releaseResumePlayback();
      resumeTutorCaptions();
      void window.pcmPlayer?.continue();
      addResumeDebugEvent("vodemo:resume_playback_hold:release", { reason });
    },
    [resumeTutorCaptions],
  );

  const armResumePlaybackHold = useCallback(() => {
    if (isHeldRef.current) return;
    isHeldRef.current = true;
    const player = window.pcmPlayer;
    // audioT0 survives the previous generation, and playedMs is derived from
    // it — leaving it stamped makes this turn's captions all fire at once.
    // This is the clear() the browser-driven resume used to do before sending.
    void player?.clear();
    holdResumePlayback();
    pauseTutorCaptions();
    void player?.pause();
    timeoutRef.current = setTimeout(
      () => releaseResumePlaybackHold("timeout"),
      RESUME_PLAYBACK_HOLD_TIMEOUT_MS,
    );
    addResumeDebugEvent("vodemo:resume_playback_hold:arm", {
      timeout_ms: RESUME_PLAYBACK_HOLD_TIMEOUT_MS,
    });
  }, [pauseTutorCaptions, releaseResumePlaybackHold]);

  useEffect(
    () => () => releaseResumePlaybackHold("unmount"),
    [releaseResumePlaybackHold],
  );

  return { armResumePlaybackHold, releaseResumePlaybackHold };
}
